import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  HelpCircle, Book, Video, Search, Users, Target, 
  MessageSquare, Calendar, Upload, Settings, Shield,
  ChevronRight, ExternalLink, Play, CheckCircle
} from 'lucide-react';

// FAQ Data
const faqData = {
  general: [
    {
      question: "What is Beluga Pro?",
      answer: "Beluga Pro is a comprehensive lead management CRM with automation capabilities similar to GoHighLevel. It helps you capture, track, and convert leads with features like pipeline management, automated workflows, appointment scheduling, and multi-channel communication."
    },
    {
      question: "How do I get started?",
      answer: "After logging in: 1) Set up your business integrations in Settings, 2) Create campaigns in Admin > Campaigns, 3) Get your tracking code from Integrations page, 4) Add leads via CSV upload, website forms, or manually, 5) Set up workflows to automate follow-ups."
    },
    {
      question: "What are the different user roles?",
      answer: "Super Admin: Manages all businesses, sees all data, configures integrations. Business Admin: Manages their business's campaigns, leads, users, and workflows. User: Can view and update leads assigned to their campaigns."
    }
  ],
  leads: [
    {
      question: "How do I add a new lead?",
      answer: "Multiple options: 1) Click 'Add Lead' on Dashboard/Pipeline, 2) Upload CSV from 'Upload Leads' page, 3) Use webhook from website forms (get code from Integrations page), 4) Connect Meta/TikTok Lead Ads via webhooks."
    },
    {
      question: "What do the lead statuses mean?",
      answer: "New: Not yet contacted. Interested: Expressed interest. Not Interested: Declined. Callback: Scheduled for follow-up. Pipeline stages track the sales journey: Contacted → Qualified → Won/Lost."
    },
    {
      question: "How do I schedule a callback?",
      answer: "Open lead details, scroll to 'Schedule Callback', choose quick options (3, 7, 14, 28 days) or custom date. Add a callback reason - this appears on the Follow-ups page and in reminder notifications."
    },
    {
      question: "How do I assign tags to leads?",
      answer: "Open a lead's details and scroll to the Tags section. Click on any tag to toggle it on/off. Create new tags in Admin > Tags. Tags can trigger automated workflows!"
    }
  ],
  workflows: [
    {
      question: "What are workflows?",
      answer: "Workflows are automated sequences triggered by events. For example: When a lead is created → Wait 5 minutes → Send welcome SMS → Add 'Welcome Sent' tag. Set up workflows in the Workflows page."
    },
    {
      question: "What triggers are available?",
      answer: "Lead Created, Tag Added, Tag Removed, Appointment Booked, Appointment Outcome, Form Submitted, Webhook Received, Manual Trigger. Each trigger can start an automated action sequence."
    },
    {
      question: "What actions can workflows perform?",
      answer: "Add/Remove Tags, Send SMS, Send Email, Move Pipeline Stage, Add to Follow-up, Wait/Delay. Actions execute in sequence based on your workflow design."
    },
    {
      question: "How do I test my workflow?",
      answer: "Create a test lead manually or via webhook. The workflow will trigger automatically if conditions match. Check the lead's activity log to see workflow actions executed."
    }
  ],
  integrations: [
    {
      question: "How do I capture leads from my website?",
      answer: "Go to Integrations page, select your campaign, click 'Generate Code'. Copy the tracking code to your website's <head> section. Use window.belugaSubmitLead({name, email, phone}) to submit leads. UTM parameters are captured automatically."
    },
    {
      question: "What are UTM parameters?",
      answer: "UTM parameters track where leads come from. Add to your ad URLs: ?utm_source=facebook&utm_medium=paid&utm_campaign=spring_sale. These appear on each lead showing the source, medium, and campaign name."
    },
    {
      question: "How do I connect Facebook/Meta Lead Ads?",
      answer: "1) Create a Facebook App at developers.facebook.com, 2) Copy webhook URL from Integrations > Meta tab, 3) Subscribe to lead events, 4) Add Page Access Token. Leads will sync automatically when forms are submitted."
    },
    {
      question: "How do I connect with Zapier?",
      answer: "Use 'Webhooks by Zapier' action. Copy the webhook URL from Integrations > Webhooks tab. Send POST request with JSON: {name, email, phone, campaign_id, source}. Works with any Zapier trigger."
    }
  ],
  notifications: [
    {
      question: "How do I set up SMS notifications?",
      answer: "Create a business or edit existing one in Admin > Businesses. On step 2 (Integrations), enter Twilio credentials: Account SID, Auth Token, Phone Number. Get these from console.twilio.com."
    },
    {
      question: "How do I set up email notifications?",
      answer: "In business settings (Admin > Businesses > Edit), add SendGrid API Key and verified sender email on the Integrations step. Get API key from app.sendgrid.com."
    },
    {
      question: "When are notifications sent?",
      answer: "Notifications are sent: 1) When a new lead is created (if enabled in user settings), 2) When workflow actions trigger SMS/email, 3) For appointment reminders (coming soon)."
    }
  ],
  appointments: [
    {
      question: "How do appointments work?",
      answer: "Create calendars in Appointments page to define availability. Leads can book appointments through booking links (coming soon), or staff can create appointments manually. View all appointments in the calendar view."
    },
    {
      question: "Can I sync with Google Calendar?",
      answer: "Google Calendar integration is planned. Once configured, appointments will sync both ways - bookings made in Beluga Pro appear in Google Calendar and vice versa."
    }
  ]
};

// Tutorial Videos Data
const tutorialVideos = {
  user: [
    {
      id: 'dashboard-overview',
      title: 'Dashboard Overview',
      description: 'Learn how to navigate the dashboard and understand your lead metrics',
      duration: '3:45',
      thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=225&fit=crop',
      topics: ['Stats cards', 'Recent leads', 'Pipeline summary', 'Quick actions']
    },
    {
      id: 'managing-leads',
      title: 'Managing Leads',
      description: 'How to view, edit, and update lead information',
      duration: '5:20',
      thumbnail: 'https://images.unsplash.com/photo-1553484771-371a605b060b?w=400&h=225&fit=crop',
      topics: ['Opening leads', 'Updating status', 'Adding notes', 'Setting revenue']
    },
    {
      id: 'pipeline-view',
      title: 'Using the Pipeline',
      description: 'Master the Kanban board to track leads through stages',
      duration: '4:15',
      thumbnail: 'https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?w=400&h=225&fit=crop',
      topics: ['Pipeline stages', 'Drag and drop', 'Filtering leads', 'Stage statistics']
    },
    {
      id: 'callbacks-followups',
      title: 'Callbacks & Follow-ups',
      description: 'Never miss a follow-up with callback scheduling',
      duration: '4:00',
      thumbnail: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=400&h=225&fit=crop',
      topics: ['Scheduling callbacks', 'Setting reminders', 'Callback reasons', 'Follow-ups page']
    },
    {
      id: 'chat-communication',
      title: 'Communicating with Leads',
      description: 'Use the Chat Tab to reach leads via WhatsApp, SMS, and Email',
      duration: '6:10',
      thumbnail: 'https://images.unsplash.com/photo-1577563908411-5077b6dc7624?w=400&h=225&fit=crop',
      topics: ['Message templates', 'Sending messages', 'Logging conversations', 'Activity history']
    }
  ],
  admin: [
    {
      id: 'business-setup',
      title: 'Business Setup',
      description: 'Configure your business and create campaigns',
      duration: '5:30',
      thumbnail: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=225&fit=crop',
      topics: ['Creating businesses', 'Adding campaigns', 'Managing users', 'Role permissions']
    },
    {
      id: 'twilio-integration',
      title: 'Twilio SMS Setup',
      description: 'Connect Twilio for SMS notifications',
      duration: '7:00',
      thumbnail: 'https://images.unsplash.com/photo-1611606063065-ee7946f0787a?w=400&h=225&fit=crop',
      topics: ['Creating Twilio account', 'Getting credentials', 'Configuring in Beluga Pro', 'Testing SMS']
    },
    {
      id: 'sendgrid-integration',
      title: 'SendGrid Email Setup',
      description: 'Configure SendGrid for email notifications',
      duration: '6:30',
      thumbnail: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=400&h=225&fit=crop',
      topics: ['Creating SendGrid account', 'Verifying sender', 'API key setup', 'Testing emails']
    },
    {
      id: 'facebook-integration',
      title: 'Facebook Lead Ads Setup',
      description: 'Connect Facebook to automatically capture leads',
      duration: '10:00',
      thumbnail: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400&h=225&fit=crop',
      topics: ['Facebook App creation', 'Webhook configuration', 'Lead subscriptions', 'Testing integration']
    },
    {
      id: 'csv-upload',
      title: 'Bulk Lead Upload',
      description: 'Import leads from CSV files',
      duration: '4:45',
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=225&fit=crop',
      topics: ['CSV format', 'Uploading files', 'Duplicate handling', 'Mapping fields']
    },
    {
      id: 'team-management',
      title: 'Team & Announcements',
      description: 'Manage your team and send announcements',
      duration: '5:15',
      thumbnail: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=225&fit=crop',
      topics: ['Adding users', 'Setting roles', 'Creating announcements', 'Team collaboration']
    }
  ]
};

// Quick Start Guide
const quickStartSteps = [
  { icon: Users, title: 'Add Your First Lead', description: 'Click "Add Lead" on the Dashboard to manually create a lead' },
  { icon: Target, title: 'Move Through Pipeline', description: 'Drag leads across stages as they progress' },
  { icon: Calendar, title: 'Schedule Follow-ups', description: 'Set callback dates with context reminders' },
  { icon: MessageSquare, title: 'Communicate', description: 'Use the Chat Tab to reach out via WhatsApp, SMS, or Email' },
  { icon: CheckCircle, title: 'Log Everything', description: 'Track all interactions in the Activity Log' }
];

const HelpPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);

  const filterFAQ = (category) => {
    if (!searchQuery) return faqData[category];
    return faqData[category].filter(
      item => 
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="help-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Help & Tutorials</h1>
        <p className="text-sm sm:text-base text-slate-500">Learn how to get the most out of Beluga Pro</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
        <Input
          placeholder="Search help articles..."
          className="pl-9 sm:pl-10 min-h-[44px]"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <Tabs defaultValue="quickstart" className="space-y-4 sm:space-y-6">
        <TabsList className="w-full grid grid-cols-3 h-auto">
          <TabsTrigger value="quickstart" className="text-xs sm:text-sm py-2.5">Quick Start</TabsTrigger>
          <TabsTrigger value="faq" className="text-xs sm:text-sm py-2.5">FAQ</TabsTrigger>
          <TabsTrigger value="videos" className="text-xs sm:text-sm py-2.5">Videos</TabsTrigger>
        </TabsList>

        {/* Quick Start Tab */}
        <TabsContent value="quickstart">
          <div className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-500" />
                  Getting Started in 5 Steps
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Follow these steps to start managing your leads effectively
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
                <div className="grid gap-3 sm:gap-4">
                  {quickStartSteps.map((step, index) => (
                    <div key={index} className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full text-white font-bold text-xs sm:text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <step.icon className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600 flex-shrink-0" />
                          <h4 className="font-semibold text-sm sm:text-base">{step.title}</h4>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-600 mt-1">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Feature Overview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <Card className="border-l-4 border-l-cyan-500">
                <CardContent className="p-4 sm:pt-6">
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-500 mb-2 sm:mb-3" />
                  <h3 className="font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Pipeline Management</h3>
                  <p className="text-xs sm:text-sm text-slate-600">
                    Visual Kanban board to track leads through New, Contacted, Qualified, Won, and Lost stages.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="pt-6">
                  <MessageSquare className="w-8 h-8 text-purple-500 mb-3" />
                  <h3 className="font-semibold mb-2">Smart Communication</h3>
                  <p className="text-sm text-slate-600">
                    Pre-built message templates for WhatsApp, SMS, and Email with one-click sending.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-amber-500">
                <CardContent className="pt-6">
                  <Calendar className="w-8 h-8 text-amber-500 mb-3" />
                  <h3 className="font-semibold mb-2">Callback Scheduling</h3>
                  <p className="text-sm text-slate-600">
                    Never forget a follow-up with preset intervals and custom date scheduling.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* FAQ Tab */}
        <TabsContent value="faq">
          <div className="space-y-6">
            <Tabs defaultValue="general">
              <TabsList className="mb-4 flex flex-wrap h-auto gap-1">
                <TabsTrigger value="general" className="text-xs sm:text-sm">General</TabsTrigger>
                <TabsTrigger value="leads" className="text-xs sm:text-sm">Leads</TabsTrigger>
                <TabsTrigger value="workflows" className="text-xs sm:text-sm">Workflows</TabsTrigger>
                <TabsTrigger value="integrations" className="text-xs sm:text-sm">Integrations</TabsTrigger>
                <TabsTrigger value="notifications" className="text-xs sm:text-sm">Notifications</TabsTrigger>
                <TabsTrigger value="appointments" className="text-xs sm:text-sm">Appointments</TabsTrigger>
              </TabsList>

              {Object.keys(faqData).map(category => (
                <TabsContent key={category} value={category}>
                  <Card>
                    <CardContent className="pt-6">
                      <Accordion type="single" collapsible className="w-full">
                        {filterFAQ(category).map((item, index) => (
                          <AccordionItem key={index} value={`item-${index}`}>
                            <AccordionTrigger className="text-left">
                              <span className="flex items-center gap-2">
                                <HelpCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                                {item.question}
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-600 pl-6">
                              {item.answer}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </TabsContent>

        {/* Video Tutorials Tab */}
        <TabsContent value="videos">
          <div className="space-y-8">
            {/* User Tutorials */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-500" />
                For All Users
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {tutorialVideos.user.map((video) => (
                  <Card key={video.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group">
                    <div className="relative">
                      <img 
                        src={video.thumbnail} 
                        alt={video.title}
                        className="w-full h-32 sm:h-40 object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 sm:transition-opacity">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-600 ml-0.5" />
                        </div>
                      </div>
                      <Badge className="absolute bottom-2 right-2 bg-black/70 text-white text-xs">
                        {video.duration}
                      </Badge>
                    </div>
                    <CardContent className="p-3 sm:p-4">
                      <h4 className="font-semibold mb-1 text-sm sm:text-base">{video.title}</h4>
                      <p className="text-xs sm:text-sm text-slate-500 mb-2 sm:mb-3 line-clamp-2">{video.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {video.topics.slice(0, 2).map((topic, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Admin Tutorials */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                For Administrators
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {tutorialVideos.admin.map((video) => (
                  <Card key={video.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group">
                    <div className="relative">
                      <img 
                        src={video.thumbnail} 
                        alt={video.title}
                        className="w-full h-32 sm:h-40 object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 sm:transition-opacity">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 ml-0.5" />
                        </div>
                      </div>
                      <Badge className="absolute bottom-2 right-2 bg-black/70 text-white text-xs">
                        {video.duration}
                      </Badge>
                    </div>
                    <CardContent className="p-3 sm:p-4">
                      <h4 className="font-semibold mb-1 text-sm sm:text-base">{video.title}</h4>
                      <p className="text-xs sm:text-sm text-slate-500 mb-2 sm:mb-3 line-clamp-2">{video.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {video.topics.slice(0, 2).map((topic, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HelpPage;
